import { useState, useEffect, useCallback } from 'react';
import {
  Box, Title, Text, Stack, Group, Button,
  TextInput, MultiSelect, Checkbox, Pagination, Loader,
  Alert, Paper,
} from '@mantine/core';
import { IconAlertCircle, IconFilter } from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import UseCaseCard from './components/UseCaseCard';
import { ivrListUseCases } from './api_scripts/ivrClient';

const PAGE_SIZE = 20;

export default function IvrBrowser() {
  const { getApiToken } = useAuth();
  const [page, setPage] = useState(1);
  const [vertical, setVertical] = useState('');
  const [platform, setPlatform] = useState('');
  const [complexity, setComplexity] = useState([]);
  const [selfServiceOnly, setSelfServiceOnly] = useState(false);

  const [applied, setApplied] = useState({ vertical: '', platform: '', complexity: [], selfServiceOnly: false });

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadPage = useCallback(async (pageNum, filters) => {
    setLoading(true);
    setError(null);
    try {
      const data = await ivrListUseCases({
        page: pageNum,
        page_size: PAGE_SIZE,
        vertical: filters.vertical || null,
        platform: filters.platform || null,
        complexity: filters.complexity,
        self_service_only: filters.selfServiceOnly ? 'true' : null,
      }, getApiToken);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getApiToken]);

  useEffect(() => {
    loadPage(page, applied);
  }, [page, applied, loadPage]);

  const applyFilters = () => {
    setPage(1);
    setApplied({ vertical, platform, complexity, selfServiceOnly });
  };

  const clearFilters = () => {
    setVertical('');
    setPlatform('');
    setComplexity([]);
    setSelfServiceOnly(false);
    setPage(1);
    setApplied({ vertical: '', platform: '', complexity: [], selfServiceOnly: false });
  };

  const hasActiveFilters = applied.vertical || applied.platform || applied.complexity.length > 0 || applied.selfServiceOnly;

  return (
    <Box style={{ display: 'flex', flexDirection: 'column' }}>

      <Box style={{ padding: '32px 40px 24px', borderBottom: '1px solid #e9ecef', background: '#ffffff' }}>
        <Title order={2} size="1.5rem" fw={700}>Documents</Title>
        <Text c="dimmed" size="sm" mt={4}>Browse SharePoint source documents</Text>
      </Box>

      <Box style={{ padding: '24px 40px 32px' }}>
        <Stack gap="lg">

          {/* Filter panel */}
          <Paper shadow="sm" p="lg" radius="md" withBorder>
            <Group gap="xs" mb="md" align="center">
              <IconFilter size={16} style={{ color: '#4c6ef5' }} />
              <Text fw={600} size="sm" c="indigo">Filters</Text>
            </Group>
            <Stack gap="sm">
              <Group gap="md" wrap="wrap">
                <TextInput
                  label="Vertical"
                  placeholder="e.g. Healthcare"
                  value={vertical}
                  onChange={(e) => setVertical(e.target.value)}
                  size="sm"
                  style={{ flex: 1, minWidth: 160 }}
                />
                <TextInput
                  label="Platform"
                  placeholder="e.g. Cisco, Amazon Connect"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  size="sm"
                  style={{ flex: 1, minWidth: 160 }}
                />
                <MultiSelect
                  label="Complexity"
                  data={['S', 'M', 'L', 'XL']}
                  value={complexity}
                  onChange={setComplexity}
                  placeholder="Any"
                  size="sm"
                  style={{ flex: 1, minWidth: 160 }}
                />
              </Group>
              <Group justify="space-between">
                <Checkbox
                  label="Self-service only"
                  checked={selfServiceOnly}
                  onChange={(e) => setSelfServiceOnly(e.currentTarget.checked)}
                  size="sm"
                  color="indigo"
                />
                <Group gap="xs">
                  {hasActiveFilters && (
                    <Button variant="subtle" size="xs" color="gray" onClick={clearFilters}>
                      Clear
                    </Button>
                  )}
                  <Button size="sm" color="indigo" onClick={applyFilters}>
                    Apply Filters
                  </Button>
                </Group>
              </Group>
            </Stack>
          </Paper>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Load error">
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <Text size="sm" c="dimmed">
              {total} use case{total !== 1 ? 's' : ''}{hasActiveFilters ? ' (filtered)' : ''}
            </Text>
          )}

          {loading && (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <Loader color="indigo" />
            </Box>
          )}

          {!loading && items.length > 0 && (
            <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {items.map((uc) => (
                <UseCaseCard key={uc.id} useCase={uc} />
              ))}
            </Box>
          )}

          {!loading && !error && items.length === 0 && total === 0 && (
            <Paper p="xl" radius="md" style={{ textAlign: 'center' }}>
              <Text c="dimmed">No use cases found. Try adjusting or clearing the filters.</Text>
            </Paper>
          )}

          {pages > 1 && (
            <Group justify="center" mt="md">
              <Pagination total={pages} value={page} onChange={setPage} color="indigo" />
            </Group>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
